import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import Formsy from 'formsy-react'

import { Button, IconButton, Table, TableContainer, TableHead, TableBody, Paper, 
        DialogTitle, DialogContent, DialogActions } from '@mui/material'
import { Add, Delete, Edit } from '@mui/icons-material'

import { C, D, Loading, SearchField, TRow, TCell, TextFieldFormsy, ConfirmDelete } from '../include'

// ==========================================
// COMPONENTE para editar/adicionar empresas
// ==========================================
const EditBusiness = ({ row, edit, api, alert, dialogClose }) => {
  // Inicializa com o valor da linha ou vazio
  const [nome, setNome] = useState(row ? row.nome : '');

  const handleSave = () => {
    if (!edit) {
      api.post('/Courts/', { name: nome })
        .then(r => {
          dialogClose();
          alert(r.msg);
        })
        .catch(e => alert(e.msg || 'Erro ao salvar.'));
    } else {
      api.put(`/Court/${row.id}`, { id: row.id, name: nome })
        .then(r => {
          dialogClose();
          alert(r.msg);
        })
        .catch(e => alert(e.msg || 'Erro ao atualizar.'));
    }
  };

  return (
    <Formsy onValidSubmit={() => null}>
      <DialogTitle style={{ minWidth: '400px' }}>
        {edit ? `Editar ${C.businessLabel}` : `Adicionar ${C.businessLabel}`}
      </DialogTitle>
      
      <DialogContent>
        <D h="1rem" />
        <TextFieldFormsy 
          value={nome} 
          onChange={v => setNome(v)} 
          type="text" 
          name="name" 
          label="Nome" 
          variant="outlined" 
          required 
        />
        <D h="2rem" />
      </DialogContent>
      
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={dialogClose}>
          Cancelar
        </Button>
        <Button variant="outlined" color="primary" onClick={handleSave}>
          Salvar
        </Button>
      </DialogActions>
    </Formsy>
  );
};


// ==========================================
// COMPONENTE PRINCIPAL: Lista de empresas cadastradas
// ==========================================
export const Business = ({ api, user, alert, dialog, dialogClose, permission }) => {
  const [searchField, setSearchField] = useState('');
  const [dataTable, setDataTable] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Ref de proteção contra o Strict Mode do React 18
  const isFirstRender = useRef(true);

  // useCallback sela a referência da função na memória
  const updateData = useCallback(() => {
    setIsLoading(true);

    api.get('/Courts', { params: { log: true } })
      .then(r => {
        // Se for nao admin filtra, se for repassa o 'r' original intacto
        const finalData = user?.role === 'admin' 
          ? r
          : r.filter(item => !item.nome?.includes('AI2')) ;

        setDataTable(finalData);
        setIsLoading(false);
      })
      .catch(() => {
        alert("Erro ao buscar dados.");
        setIsLoading(false);
      });
  }, [api, alert, user]);

  // useEffect "vacinado" com a trava
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false; // Tranca a porta instantaneamente
      updateData();
    }
  }, [updateData]);

  // abre dialog de adição/edição
  const openEditDialog = (row = null, isEdit = false) => {
    dialog(
      <EditBusiness 
        edit={isEdit} 
        row={row} 
        api={api} 
        alert={alert} 
        dialogClose={() => {
          dialogClose();
          updateData();
        }} 
      />
    );
  };


  // abre dialogo de exclusao
  const openDeleteDialog = (row) => {
    dialog(
      <ConfirmDelete
        msg="Tem certeza que deseja excluir a empresa:"
        itemName={row.nome}
        onCancel={dialogClose}
        onConfirm={() => {
          api.delete(`/Court/${row.id}`)
            .then(r => {
              alert(r.msg);
              updateData(); 
              dialogClose();
            })
            .catch(e => alert(e.msg || 'Erro ao excluir a empresa.'));
        }}
      />
    );
  };

  // Filtra os dados na memória (useMemo garante que só rode quando a busca ou os dados mudarem)
  const filteredData = useMemo(() => {
    if (!searchField) return dataTable;
    
    const searchLower = searchField.toLowerCase().replace(/\s/g, '');
    return dataTable.filter(row => {
      const rowValues = Object.values(row).map(v => String(v)).join('');
      return rowValues.toLowerCase().replace(/\s/g, '').includes(searchLower);
    });
  }, [dataTable, searchField]);

  return (
    <D className="col e aF scD" style={{ padding: '2rem' }}>
      {permission === 'w' && (
        <>
          <D className="row w aF">
            <SearchField 
              value={searchField} 
              onChange={v => setSearchField(v)} 
            />
            <D e />
            <D size="2rem" />
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => openEditDialog(null, false)}
            >
              <Add /> {C.businessLabel}
            </Button>
          </D>
          <D h="1rem" />
        </>
      )}
      
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer style={{ height: '100%' }}>
          <Table stickyHeader>
            <TableHead>
              <TRow sx={{ height: '3rem', background: '#ccc' }}>
                {permission === 'w' && <TCell align="center">Editar</TCell>}
                {permission === 'w' && <TCell align="center">Excluir</TCell>}
                <TCell width="100%">Nome ({dataTable.length} empresas)</TCell>
              </TRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TRow>
                  <TCell component="th" align="center" colSpan={3}>
                    <Loading />
                  </TCell>
                </TRow>
              ) : filteredData.length > 0 ? (
                filteredData.map(row => (
                  <TRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    {permission === 'w' && (
                      <TCell align="center" component="th">
                        <IconButton 
                          style={{ color: '#aaa' }} 
                          onClick={() => openEditDialog(row, true)}
                        >
                          <Edit />
                        </IconButton>
                      </TCell>
                    )}

                    {permission === 'w' && (
                      <TCell align="center" component="th">
                        <IconButton 
                          style={{ color: '#aaa' }} 
                          onClick={() => openDeleteDialog(row)}
                        >
                          <Delete />
                        </IconButton>
                      </TCell>
                    )}
                    <TCell component="th">{row.nome}</TCell>
                  </TRow>
                ))
              ) : (
                <TRow>
                  <TCell component="th" align="center" colSpan={3}>
                    Nenhuma empresa encontrada
                  </TCell>
                </TRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </D>
  );
};